"use client";

import * as Sentry from "@sentry/nextjs";
import { Button, Card } from "@texturehq/edges";
import { Component, type ErrorInfo, type ReactNode } from "react";
import { captureError } from "@/lib/sentry/error-tracking";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  title?: string;
  level?: "page" | "component";
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Error Boundary caught an error:", error, errorInfo);

    captureError(error, {
      feature: "error-boundary",
      metadata: {
        componentStack: errorInfo.componentStack,
        level: this.props.level || "component",
      },
    });

    Sentry.withScope((scope) => {
      scope.setContext("errorBoundary", {
        componentStack: errorInfo.componentStack,
        level: this.props.level || "component",
      });
      Sentry.captureException(error);
    });
  }

  private handleRetry = () => {
    this.setState({ hasError: false });
  };

  private handleGoHome = () => {
    window.location.href = "/";
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const isPageLevel = this.props.level === "page";

      return (
        <div className={isPageLevel ? "flex min-h-screen items-center justify-center p-4" : ""}>
          <Card>
            <div className="flex flex-col items-center justify-center gap-4 p-6" data-testid="error-boundary">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {this.props.title || "Something went wrong"}
              </h2>
              <p className="text-center text-red-600 dark:text-red-400">
                {this.state.error?.message || "An unexpected error occurred"}
              </p>
              <p className="text-center text-sm text-gray-600 dark:text-gray-400">
                {isPageLevel
                  ? "We've been notified and are working to fix this issue."
                  : "This component encountered an error. You can try again or continue using other parts of the application."}
              </p>
              <div className="flex gap-3">
                <Button onClick={this.handleRetry} variant="primary">
                  Try Again
                </Button>
                {isPageLevel && (
                  <Button onClick={this.handleGoHome} variant="secondary">
                    Go to Home
                  </Button>
                )}
              </div>
              {process.env.NODE_ENV === "development" && this.state.error?.stack && (
                <details className="mt-4 w-full">
                  <summary className="cursor-pointer text-sm text-gray-600">
                    View Error Details (Development Only)
                  </summary>
                  <pre className="mt-2 overflow-auto rounded bg-gray-100 p-3 text-xs dark:bg-gray-800">
                    {this.state.error.stack}
                  </pre>
                </details>
              )}
            </div>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
